import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authService.getProfile();
      // 后端 ResponseInterceptor 自动包装: { success, code, data: { user, permissions, role } }
      // axios 拦截器 return response.data → res = { success, code, data: { user, ... } }
      const profileData = res?.data || res;
      if (profileData && profileData.user) {
        setAdmin(profileData.user);
        setPermissions(profileData.permissions || []);
      }
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      // 如果 401，通常 request.js 会处理跳转，这里仅清理状态
      setAdmin(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [fetchProfile]);

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    // 超级管理员拥有所有权限
    if (admin?.role === 'super_admin' || permissions.includes('super_admin:all')) return true;
    return permissions.includes(permission);
  }, [admin, permissions]);

  const hasAnyPermission = useCallback((requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    if (admin?.role === 'super_admin' || permissions.includes('super_admin:all')) return true;
    return requiredPermissions.some(p => permissions.includes(p));
  }, [admin, permissions]);

  const logout = () => {
    authService.logout();
    setAdmin(null);
    setPermissions([]);
  };

  const value = {
    admin,
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    fetchProfile,
    logout
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
